// Import common styles
@use "common" as *;

// Custom variables for nav-generator
:root {
  --colorWhite: #ffffff;
  --colorBlack: #000000;
  --colorBlacklight: #222222;
  --spaceSizeAuto: auto;

  // Flexbox column width
  --flexColumnWidth: 100%;
}

// Typography mixins (nav-generator specific)
@mixin h1 {
  font-size: var(--fontSize1);
}

@mixin h2 {
  font-size: var(--fontSize2);
}

@mixin h3 {
  font-size: var(--fontSize3);
}

@mixin h4 {
  font-size: var(--fontSize4);
}

@mixin h5 {
  font-size: var(--fontSize5);
}

// Flexbox column layout (nav-generator specific)
// 2 columns
@media only screen and (min-width: #{$gridColumnWidth * 2}) {
  :root {
    --flexColumnWidth: calc(50% - var(--gridGapWidth) / 2);
  }
}

// 3 columns
@media only screen and (min-width: #{$gridColumnWidth * 3}) {
  :root {
    --flexColumnWidth: calc(33.333% - var(--gridGapWidth) * 2 / 3);
  }
}

// 4 columns
@media only screen and (min-width: #{$gridColumnWidth * 4}) {
  :root {
    --flexColumnWidth: calc(25% - var(--gridGapWidth) * 3 / 4);
  }
}

// 5 columns
@media only screen and (min-width: #{$gridColumnWidth * 5}) {
  :root {
    --flexColumnWidth: calc(20% - var(--gridGapWidth) * 4 / 5);
  }
}

// 6 columns
@media only screen and (min-width: #{$gridColumnWidth * 6}) {
  :root {
    --flexColumnWidth: calc(16.666% - var(--gridGapWidth) * 5 / 6);
  }
}

// nav-generator specific body style
html {
  height: auto !important;
}

body {
  background: var(--colorBlack);
  min-height: 100vh;
  height: auto !important;
  padding: var(--spaceSize2);
  padding-bottom: 80px;
  box-sizing: border-box;
}

#fav,
#command {
  margin: auto;

  .title {
    @include h1;
    padding: var(--spaceSize2);
    text-transform: uppercase;
    text-align: left;
    border-top: var(--borderSize2) solid var(--colorBorderMain);
    border-bottom: var(--borderSize2) solid var(--colorBorderMain);
    font-weight: var(--fontWeightBold);
    display: flex;
    align-items: center;
    justify-content: space-between;

    .action-bar {
      display: inline-flex;
      gap: var(--borderSize4);
    }
  }

  .header {
    @include h3;
    text-transform: uppercase;
    background: var(--colorBgSecondary);
    border-left: var(--borderSize4) solid var(--colorBorderMain);
    font-weight: var(--fontWeightBold);
    box-shadow:
      0 2px 2px -1px var(--colorShadow),
      0 4px 4px -2px var(--colorShadow);
  }

  .title,
  .header,
  .block,
  .tabs,
  .commands,
  form {
    width: 100%;
  }

  .commands {
    display: flex;
    flex-wrap: wrap;
    margin-top: var(--spaceSize4);
    align-items: center;
    justify-content: space-evenly;
    gap: var(--spaceSize2);
    margin-bottom: var(--spaceSize3);

    a[role="button"],
    button {
      text-align: center !important;
    }

    > * {
      flex-grow: 1;
      max-width: 210px;
    }
  }

  &#fav {
    display: flex;
    flex-wrap: wrap;
    gap: var(--spaceSize3) var(--gridGapWidth);

    > .link {
      width: var(--flexColumnWidth);
    }

    .header {
      position: sticky;
      z-index: 0;
      top: 0;
      user-select: none;
      padding: var(--spaceSize2);
    }

    .block,
    .codeBlockWrapper {
      width: 100%;
    }

    // Unify the code-block wrapper background with the page background so
    // the code reads as part of its containing tab/section instead of as a
    // floating gray panel. The wrapper border still defines the boundary;
    // the banner gets a border-bottom (from common.scss) for separation
    // between header and content. Also pulls the inner Prism `<pre>` and
    // the `.block.codeBlock` element onto the same surface so token
    // highlights sit on a clean white background.
    .codeBlockWrapper {
      background: var(--colorBgMain);

      .codeBlockBanner,
      .codeBlockContent,
      .codeBlockContent .block.codeBlock,
      .codeBlockContent pre[class*="language-"] {
        background: var(--colorBgMain);
      }
    }

    .block {
      background: var(--colorBgSecondary);

      &:focus {
        background: var(--colorBgSecondary) !important;
      }

      &.codeBlock {
        padding: var(--spaceSize2);
        overflow-x: auto;
        font-family: var(--fontFamilyMonospace);
        font-size: var(--fontSize4);
        margin: 0;

        // Prism.js syntax highlighting overrides
        &[class*="language-"],
        code[class*="language-"] {
          margin: 0;
        }
      }

      &.htmlBlock {
        padding: var(--spaceSize2);
        overflow: hidden;
      }

      &.navBlock {
        padding: var(--spaceSize4);
        // Flex-wrap grid with per-link column width so embedded schemas
        // lay out like the top level. Match the top-level #fav gap
        // (--spaceSize3 row, --gridGapWidth col) so --flexColumnWidth
        // math stays consistent — otherwise hardcoded gaps overflow the
        // 6-column row and only 5 items fit.
        display: flex;
        flex-wrap: wrap;
        gap: var(--spaceSize3) var(--gridGapWidth);

        > .link {
          width: var(--flexColumnWidth);
        }
      }
    }

    .tabs {
      background: var(--colorBgSecondary);
      display: flex;
      flex-wrap: wrap;
      // 1px gap lets the bar bg (--colorBgSecondary) peek between tabs,
      // so each tab reads as its own button instead of a continuous strip.
      gap: 1px;

      tab {
        flex-shrink: 0;
        font-size: var(--fontSize3);
        // Distinct shade from the bar so unselected tabs clearly read as
        // clickable buttons (lighter than the bar in light theme, darker
        // in dark theme — either way they pop out).
        background: var(--colorBgMain);
        padding: var(--spaceSize2) var(--spaceSize4);
        user-select: none;
        cursor: pointer;
        text-transform: uppercase;

        &:hover,
        &:focus {
          // Theme-agnostic hover feedback; works with any bg/foreground pair.
          opacity: 0.7;
        }

        &.selected {
          background: var(--colorBorderMain);
          color: var(--colorBgMain);
          font-weight: var(--fontWeightSemiBold);
        }
      }
    }

    .link {
      word-break: break-all;

      &.navigating {
        opacity: 0.7;
        background: var(--colorBgHover);
        border-color: var(--colorBorderMain);
        box-shadow: inset 0 0 0 var(--borderSize1) var(--colorBorderMain);
      }
      mark.search-highlight {
        background: var(--colorTextLink);
        color: var(--colorBgMain);
        padding: 1px var(--borderSize1);
        border-radius: var(--borderSize1);
        font-weight: var(--fontWeightBold);
      }
    }
  }

  &#command {
    display: flex;
    flex-direction: column;
    max-width: var(--maxBodyWidth);
    min-height: 100%;
    height: 100%;

    #input {
      background: var(--colorBgMain);
      border: var(--borderSize1) solid var(--colorBorderSecondary);
      padding: var(--spaceSize1) var(--spaceSize2);
      width: 100%;
      font-family: var(--fontFamilyMonospace);
      flex-grow: 1;
      white-space: nowrap;
    }
  }
}

.search-result-count {
  margin-bottom: var(--spaceSize4);
}

#searchForm {
  position: fixed;
  bottom: var(--spaceSize1);
  left: var(--spaceSize2);
  right: var(--spaceSize2);
  width: auto;
  z-index: 1;

  .search-container {
    position: relative;
    background: var(--colorBgMain);
    border: var(--borderSize1) solid var(--colorBorderMain);
    border-radius: 8px;
    box-shadow:
      0 -2px 8px var(--colorShadow),
      0 -4px 16px var(--colorShadow);
    padding: 0 var(--spaceSize2);
    backdrop-filter: blur(8px);
    transition: all 0.2s ease;

    &:focus-within {
      box-shadow:
        0 -4px 12px var(--colorShadow),
        0 -8px 24px var(--colorShadow),
        0 0 0 2px var(--colorTextLink);
    }
  }

  .search-input-wrapper {
    display: flex;
    align-items: center;
    gap: var(--spaceSize2);
    position: relative;
  }

  .search-icon {
    font-size: var(--fontSize2);
    flex-shrink: 0;
    opacity: 0.6;
  }

  #search {
    background: transparent !important;
    outline: none !important;
    box-shadow: none !important;
    border: none !important;
    padding: var(--spaceSize2);
    width: 100%;
    flex: 1;
    font-size: var(--fontSize3);
    font-weight: var(--fontWeightSemiBold);

    &::placeholder {
      color: var(--colorTextMain);
      opacity: 0.5;
    }
  }

  .search-clear-btn,
  .search-help-btn {
    background: transparent;
    border: var(--borderSize1) solid var(--colorBorderSecondary);
    border-radius: 50%;
    width: 28px;
    height: 28px;
    min-width: 28px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: var(--fontSize4);
    flex-shrink: 0;
    transition: all 0.15s ease;

    &:hover {
      background: var(--colorBgSecondary);
      border-color: var(--colorBorderMain);
      transform: scale(1.1);
    }

    &:active {
      transform: scale(0.95);
    }
  }

  .search-clear-btn {
    color: var(--colorTextMain);
    font-weight: var(--fontWeightBold);
  }

  .search-help-btn {
    color: var(--colorTextLink);
    font-weight: var(--fontWeightBold);
  }

  .search-help-popup {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 0;
    right: 0;
    background: var(--colorBgSecondary);
    border: var(--borderSize1) solid var(--colorBorderMain);
    border-radius: 8px;
    padding: var(--spaceSize3);
    box-shadow:
      0 4px 12px var(--colorShadow),
      0 8px 24px var(--colorShadow);
    animation: slideUp 0.2s ease-out;
    z-index: 10;
  }

  .search-help-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: var(--fontSize3);
    font-weight: var(--fontWeightBold);
    margin-bottom: var(--spaceSize2);
    padding-bottom: var(--spaceSize2);
    border-bottom: var(--borderSize1) solid var(--colorBorderSecondary);
  }

  .search-help-close {
    background: transparent;
    border: none;
    font-size: var(--fontSize2);
    cursor: pointer;
    padding: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.15s ease;

    &:hover {
      background: var(--colorBgHover);
    }
  }

  .search-help-content {
    display: flex;
    flex-direction: column;
    gap: var(--spaceSize2);
  }

  .search-help-item {
    display: flex;
    gap: var(--spaceSize3);
    align-items: flex-start;
    font-size: var(--fontSize4);

    code {
      background: var(--colorBgMain);
      color: var(--colorTextLink);
      padding: 2px 8px;
      border-radius: 4px;
      font-family: var(--fontFamilyMonospace);
      font-size: var(--fontSize5);
      font-weight: var(--fontWeightSemiBold);
      white-space: nowrap;
      flex-shrink: 0;
      min-width: 80px;
    }

    span {
      opacity: 0.9;
      line-height: 1.4;
    }
  }
}

button[role="button"] {
  text-align: center;
  display: inline-block !important;
}

load {
  box-sizing: border-box;
  border-top: var(--borderSize3) solid var(--colorTextLink);
  display: block;
  position: fixed;
  left: 0;
  top: 0;
}

// Modal styles are in common.scss

// nav-generator specific responsive styles
@media only screen and (max-width: #{$gridColumnWidth * 3}) {
  :root {
    --gridGapHeight: 0.4rem;
    --gridGapWidth: 0.5rem;
  }
}

@media only screen and (max-width: #{$gridColumnWidth * 2}) {
  #fav .title,
  #fav .header,
  #fav .block {
    padding: var(--spaceSize1) var(--spaceSize2);
  }

  #fav,
  #command {
    padding: var(--spaceSize2);
    max-width: 100vw;
    overflow-x: hidden;

    .title {
      .action-bar {
        display: none;
      }
    }
  }

  .commands > * {
    max-width: none !important;
  }
}

// TV links never appear in the normal navigation view, even without a schema directive.
:root:not([data-tv-mode]) #fav [data-tv-link] {
  display: none !important;
}

// All large-display overrides stay local; shared styles and ordinary links are unchanged.
:root[data-tv-mode] {
  --fontSize1: clamp(1.75rem, 1.5rem + 1vw, 4rem);
  --fontSize2: clamp(1.5rem, 1.25rem + 0.75vw, 3rem);
  --fontSize3: clamp(1.25rem, 1rem + 0.65vw, 2.5rem);
  --fontSize4: clamp(1.125rem, 1rem + 0.4vw, 2rem);
  --fontSize5: clamp(1rem, 0.9rem + 0.3vw, 1.5rem);
  --gridGapWidth: 1.5rem;
  --flexColumnWidth: 100%;

  // Keep tiles readable rather than inheriting the compact six-column layout.
  @media (min-width: 48rem) {
    --flexColumnWidth: calc(50% - var(--gridGapWidth) / 2);
  }
  @media (min-width: 80rem) {
    --flexColumnWidth: calc(33.333% - var(--gridGapWidth) * 2 / 3);
  }
  @media (min-width: 120rem) {
    --flexColumnWidth: calc(25% - var(--gridGapWidth) * 3 / 4);
  }

  body {
    font-size: var(--fontSize3);
    line-height: 1.5;
  }

  button,
  a[role="button"] {
    padding: 2rem 1.5rem;
    min-height: 3rem;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  input,
  select,
  textarea {
    font-size: var(--fontSize3);
    min-height: 3rem;
    padding: 1rem;
  }

  button,
  a[role="button"],
  tab,
  input,
  select,
  textarea {
    &:focus-visible {
      outline: 4px solid var(--colorFocusRing);
      outline-offset: 4px;
    }
  }

  #fav,
  #command {
    .title,
    .header {
      padding: 1rem;
      overflow-wrap: anywhere;
    }

    .title {
      flex-wrap: wrap;
    }

    .commands > * {
      max-width: none;
    }
  }

  #fav {
    row-gap: 1.5rem;

    .title .action-bar {
      display: none;
    }

    // The standalone schema directive opts into a link-only TV view.
    &[data-tv-schema] {
      > :not(.title):not([data-tv-link]) {
        display: none !important;
      }

      > .title {
        display: flex !important;
      }
    }

    .link {
      min-width: 0;
      word-break: normal;
      overflow-wrap: anywhere;

      img {
        width: 1.5em;
        height: 1.5em;
        flex-shrink: 0;
        object-fit: contain;
      }
    }

    .tabs {
      gap: 0.5rem;

      tab {
        padding: 1.5rem;
        max-width: 100%;
        overflow-wrap: anywhere;
      }
    }
  }

  .dropdown {
    max-width: 100%;

    .dropdown-content {
      max-width: calc(100vw - 4rem);

      button,
      a[role="button"] {
        white-space: normal;
        overflow-wrap: anywhere;
      }
    }
  }

  .codeBlockBanner,
  .codeBlockActions {
    flex-wrap: wrap;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
